import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RotateCw, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SHOP_SWITCH_MOTION } from '@/lib/shopSwitch';
import { getShopSolidColor, initials } from '@/components/dashboard-v3/sidebar/shopSwitcherUtils';
import { cn } from '@/lib/utils';

export interface ShopSwitchVisualState {
  name: string;
  phase: 'preparing' | 'loading' | 'error';
  slow: boolean;
}

export function ShopSwitchOverlay({
  state,
  onRetry,
  onBack,
  onExited,
}: {
  state: ShopSwitchVisualState | null;
  onRetry: () => void;
  onBack: () => void;
  onExited: () => void;
}) {
  const { t } = useTranslation('common');
  const reduced = useReducedMotion();
  const panel = useRef<HTMLDivElement>(null);
  const active = Boolean(state);

  useEffect(() => {
    if (!active) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panel.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [active]);

  const solidColor = getShopSolidColor(state?.name ?? 'Velko');

  return createPortal(
    <AnimatePresence onExitComplete={onExited}>
      {state && (
        <motion.div
          ref={panel}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="shop-switch-name"
          aria-describedby="shop-switch-status"
          data-testid="shop-switch-overlay"
          className="shop-switch-overlay fixed inset-0 z-50 flex min-h-dvh flex-col items-center justify-between overflow-y-auto bg-white px-6 py-10 outline-none select-none dark:bg-[#0B0F17] sm:py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0 : SHOP_SWITCH_MOTION.reveal }}
          onKeyDown={(event) => {
            if (event.key !== 'Tab') return;
            const buttons = panel.current?.querySelectorAll<HTMLButtonElement>('button');
            if (!buttons?.length) {
              event.preventDefault();
              return;
            }
            const first = buttons[0];
            const last = buttons[buttons.length - 1];
            if (
              event.shiftKey &&
              (document.activeElement === first || document.activeElement === panel.current)
            ) {
              event.preventDefault();
              last?.focus();
            } else if (
              !event.shiftKey &&
              (document.activeElement === last || document.activeElement === panel.current)
            ) {
              event.preventDefault();
              first?.focus();
            }
          }}
        >
          {/* Header : Vrai logo officiel Velko */}
          <div className="flex items-center justify-center select-none">
            <img
              src="/brand/velko-lockup.svg"
              alt="Velko"
              className="h-6 w-auto object-contain dark:hidden sm:h-7"
            />
            <img
              src="/brand/velko-lockup-white.svg"
              alt="Velko"
              className="hidden h-6 w-auto object-contain dark:block sm:h-7"
            />
          </div>

          {/* Unité centrale de transition */}
          <div className="my-auto flex w-full max-w-sm flex-col items-center text-center">
            {/* Avatar solide de la boutique */}
            <motion.div
              initial={reduced ? false : { opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: reduced ? 0 : 0.2, ease: 'easeOut' }}
              className={cn(
                'flex h-20 w-20 items-center justify-center rounded-2xl font-head text-2xl font-bold shadow-md ring-1 ring-black/5 select-none dark:ring-white/10 sm:h-22 sm:w-22 sm:text-3xl',
                solidColor.bg,
                solidColor.text,
              )}
            >
              {initials(state.name)}
            </motion.div>

            {/* Nom de la boutique */}
            <h1
              id="shop-switch-name"
              className="mt-5 w-full break-words font-head text-2xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-[26px]"
            >
              {state.name}
            </h1>

            {/* Message de statut */}
            <p
              id="shop-switch-status"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="mt-2 text-sm font-medium text-slate-500 dark:text-slate-400"
            >
              {state.phase === 'error'
                ? t('shop_switch.error')
                : state.slow
                  ? t('shop_switch.slow')
                  : t('shop_switch.opening')}
            </p>

            {/* Jauge de progression épurée */}
            {state.phase !== 'error' && (
              <div
                aria-hidden="true"
                className="relative mt-7 h-1 w-44 overflow-hidden rounded-full bg-slate-100 dark:bg-charcoal-800"
              >
                <motion.div
                  className="absolute inset-y-0 w-20 rounded-full bg-lime-500"
                  animate={reduced ? { x: '50%' } : { x: ['-100%', '220%'] }}
                  transition={
                    reduced
                      ? { duration: 0 }
                      : {
                          duration: 1.4,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }
                  }
                />
              </div>
            )}

            {/* Actions claires en cas d'erreur */}
            {state.phase === 'error' && (
              <div className="mt-8 flex w-full flex-col gap-2.5">
                <div className="mb-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-rose-600 dark:text-rose-400">
                  <AlertTriangle className="size-4 shrink-0" />
                  <span>{t('shop_switch.error')}</span>
                </div>
                <Button
                  className="min-h-11 w-full rounded-xl bg-slate-900 font-semibold text-white transition-colors hover:bg-slate-800 dark:bg-lime-500 dark:text-charcoal-950 dark:hover:bg-lime-400"
                  onClick={onRetry}
                >
                  <RotateCw className="mr-2 size-4" />
                  {t('shop_switch.retry')}
                </Button>
                <Button
                  variant="outline"
                  className="min-h-11 w-full rounded-xl whitespace-normal border-slate-200 text-slate-700 hover:bg-slate-50 dark:border-charcoal-700 dark:bg-charcoal-900 dark:text-slate-200 dark:hover:bg-charcoal-800"
                  onClick={onBack}
                >
                  <ArrowLeft className="mr-2 size-4 shrink-0" />
                  {t('shop_switch.back')}
                </Button>
              </div>
            )}
          </div>

          {/* Footer contextuel discret */}
          <div className="text-[11px] font-medium tracking-wider text-slate-600 uppercase select-none dark:text-slate-400">
            {t('shop_switch.destination')}
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
